import React, { useState, useEffect } from 'react';
import { Select, Card, Button, message, Checkbox, Radio, Row, Col } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import CityDataChart from './CityDataChart';
import CityDataTable from './CityDataTable';
import { loadCityIndicatorData, getAvailableIndicators, getYearRange, filterByYearRange, exportParsedData } from '../utils/cityWaterDataUtils';

const { Option } = Select;

const CityWaterUsagePage = () => {
  const [cities] = useState([
    { name: '广州', fileName: '广州_指标_观测.csv' },
    { name: '深圳', fileName: '深圳_指标_观测.csv' },
    { name: '东莞', fileName: '东莞_指标_观测.csv' }
  ]);
  
  const [selectedCity, setSelectedCity] = useState('深圳');
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [cityData, setCityData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearRange, setYearRange] = useState({ min: null, max: null });
  const [selectedYearRange, setSelectedYearRange] = useState({ start: null, end: null });
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'compare'

  // 加载城市数据
  const loadCityData = async (cityName) => {
    setLoading(true);
    try {
      const city = cities.find(c => c.name === cityName);
      if (!city) throw new Error(`未找到城市: ${cityName}`);
      
      const parsed = await loadCityIndicatorData(city.fileName);
      setCityData(parsed);
      
      const indicators = getAvailableIndicators(parsed);
      setAvailableIndicators(indicators);
      
      const range = getYearRange(parsed);
      setYearRange(range);
      setSelectedYearRange({ start: range.min, end: range.max });
      
      // 默认选择前3个指标
      setSelectedIndicators(indicators.slice(0, Math.min(3, indicators.length)));
      
    } catch (error) {
      message.error(`加载城市数据失败: ${error.message}`);
      setCityData(null);
      setAvailableIndicators([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadCityData(selectedCity);
  }, [selectedCity]); // eslint-disable-line react-hooks/exhaustive-deps

  // 筛选数据
  useEffect(() => {
    if (!cityData || !selectedYearRange.start || !selectedYearRange.end) {
      setFilteredData(cityData);
      return;
    }
    
    const filtered = filterByYearRange(cityData, selectedYearRange.start, selectedYearRange.end);
    setFilteredData(filtered);
  }, [cityData, selectedYearRange]);

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setSelectedIndicators([]);
  };

  const handleIndicatorChange = (checkedValues) => {
    setSelectedIndicators(checkedValues);
  };

  const handleYearRangeChange = (field, value) => {
    setSelectedYearRange(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = () => {
    if (!filteredData) {
      message.warning('没有数据可以导出');
      return;
    }
    
    exportParsedData(filteredData, `${selectedCity}_用水数据_${selectedYearRange.start}-${selectedYearRange.end}`);
    message.success('数据导出成功');
  };

  const handleRefresh = () => {
    loadCityData(selectedCity);
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* 控制面板 */}
      <Card title="东江流域主要供水城市的选择" style={{ marginBottom: '20px' }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <div>
              <label>城市选择:</label>
              <Select
                value={selectedCity}
                onChange={handleCityChange}
                style={{ width: '100%', marginTop: '4px' }}
              >
                {cities.map(city => (
                  <Option key={city.name} value={city.name}>{city.name}</Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col span={6}>
            <div>
              <label>起始年份:</label>
              <Select
                value={selectedYearRange.start}
                onChange={(value) => handleYearRangeChange('start', value)}
                style={{ width: '100%', marginTop: '4px' }}
                disabled={!yearRange.min}
              >
                {yearRange.min && Array.from(
                  { length: yearRange.max - yearRange.min + 1 }, 
                  (_, i) => yearRange.min + i
                ).map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col span={6}>
            <div>
              <label>结束年份:</label>
              <Select
                value={selectedYearRange.end}
                onChange={(value) => handleYearRangeChange('end', value)}
                style={{ width: '100%', marginTop: '4px' }}
                disabled={!yearRange.max}
              >
                {yearRange.max && Array.from(
                  { length: yearRange.max - yearRange.min + 1 }, 
                  (_, i) => yearRange.min + i
                ).map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col span={6}>
            <div>
              <label>展示模式:</label>
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                style={{ marginTop: '4px' }}
              >
                <Radio value="single">单城市</Radio>
                <Radio value="compare" disabled>多城市对比</Radio>
              </Radio.Group>
            </div>
          </Col>
        </Row>
        
        <Row style={{ marginTop: '16px' }}>
          <Col span={18}>
            <div>
              <label>指标选择 ({selectedIndicators.length}/{availableIndicators.length}):</label>
              <div style={{
                marginTop: '8px',
                maxHeight: availableIndicators.length > 6 ? '100px' : '80px',
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px',
                backgroundColor: '#fafafa'
              }}>
                <Checkbox.Group
                  options={availableIndicators.map(ind => ({
                    label: ind.length > 12 ? `${ind.substring(0, 12)}...` : ind,
                    value: ind
                  }))}
                  value={selectedIndicators}
                  onChange={handleIndicatorChange}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}
                />
              </div>
              {selectedIndicators.length > 4 && (
                <div style={{ fontSize: '12px', color: '#faad14', marginTop: '4px' }}>
                  提示: 选择过多指标可能影响图表显示效果
                </div>
              )}
            </div>
          </Col>
          
          <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              style={{ marginRight: '8px' }}
            >
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!filteredData}
            >
              导出数据
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 数据展示 */}
      <div style={{ display: 'grid', gap: '20px' }}>
        <Card
          title={`${selectedCity}城市用水数据可视化`}
          style={{ overflow: 'hidden' }}
        >
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <CityDataChart
              data={filteredData}
              indicators={selectedIndicators}
              loading={loading}
              city={selectedCity}
            />
          </div>
        </Card>
        
      </div>
    </div>
  );
};

export default CityWaterUsagePage;